import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - add token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, error => Promise.reject(error));

// Response interceptor - handle auth errors
api.interceptors.response.use(
  response => response,
  error => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  resetPassword: (data) => api.put('/auth/reset-password', data)
};

// Users
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  toggleStatus: (id) => api.put(`/users/${id}/toggle-status`),
  resetPassword: (id) => api.put(`/users/${id}/reset-password`)
};

// Roles
export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getOne: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`)
};

// Customers
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  block: (id) => api.put(`/customers/${id}/block`),
  unblock: (id) => api.put(`/customers/${id}/unblock`),
  assignPlan: (id, planId) => api.put(`/customers/${id}/assign-plan`, { planId }),
  getPayments: (id) => api.get(`/customers/${id}/payments`),
  resetPassword: (id, data) => api.put(`/customers/${id}/reset-password`, data || {})
};

// Plans
export const plansAPI = {
  getAll: (params) => api.get('/plans', { params }),
  getOne: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
  toggleStatus: (id) => api.put(`/plans/${id}/toggle-status`),
  toggleVisibility: (id) => api.put(`/plans/${id}/toggle-visibility`),
  createManualReceipt: (planId, data) => api.post(`/plans/${planId}/manual-receipt`, data)
};

// Resellers
export const resellersAPI = {
  getAll: (params) => api.get('/resellers', { params }),
  getOne: (id) => api.get(`/resellers/${id}`),
  create: (data) => api.post('/resellers', data),
  update: (id, data) => api.put(`/resellers/${id}`, data),
  delete: (id) => api.delete(`/resellers/${id}`),
  toggleStatus: (id) => api.put(`/resellers/${id}/toggle-status`)
};

// Activation Codes
export const activationCodesAPI = {
  getAll: (params) => api.get('/activation-codes', { params }),
  getOne: (id) => api.get(`/activation-codes/${id}`),
  create: (data) => api.post('/activation-codes', data),
  delete: (id) => api.delete(`/activation-codes/${id}`),
  validate: (code) => api.post('/activation-codes/validate', { code })
};

// Subscriptions
export const subscriptionsAPI = {
  getAll: (params) => api.get('/subscriptions', { params }),
  getOne: (id) => api.get(`/subscriptions/${id}`),
  cancel: (id, data) => api.put(`/subscriptions/${id}/cancel`, data),
  renew: (id) => api.put(`/subscriptions/${id}/renew`),
  processExpired: () => api.post('/subscriptions/process-expired')
};

// Payments
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  markPaid: (id, data) => api.put(`/payments/${id}/mark-paid`, data),
  markFailed: (id, data) => api.put(`/payments/${id}/mark-failed`, data),
  sendReminder: (id) => api.post(`/payments/${id}/send-reminder`),
  exportCSV: (params) => api.get('/payments/export/csv', { params, responseType: 'blob' })
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats')
};

// Activity Logs
export const activityLogsAPI = {
  getAll:  (params) => api.get('/activity-logs', { params }),
  getOne:  (id)     => api.get(`/activity-logs/${id}`)
};

// Usage tracking
export const usageAPI = {
  getAll:          (params)               => api.get('/usage', { params }),
  getForCustomer:  (customerId)           => api.get(`/usage/customer/${customerId}`),
  recordAiQuery:   (customerId, count=1)  => api.post(`/usage/customer/${customerId}/ai-query`,       { count }),
  recordReceiptScan: (customerId, count=1)=> api.post(`/usage/customer/${customerId}/receipt-scan`,   { count }),
  recordManualInvoice: (customerId,count=1)=>api.post(`/usage/customer/${customerId}/manual-invoice`, { count }),
  recordApiTokens: (customerId, count=1)  => api.post(`/usage/customer/${customerId}/api-tokens`,     { count }),
  adjustUsage:     (usageId, data)        => api.put(`/usage/${usageId}/adjust`, data),
  resetUsage:      (usageId, type)        => api.put(`/usage/${usageId}/reset`,  { type })
};

export default api;
